import { createTheme, ThemeOptions } from "@mui/material/styles";
import { pink, yellow, red, blue, green } from "@mui/material/colors";

import {
  brandPrimary,
  brandPrimaryFocus,
  brandSecondary,
  brandSecondaryFocus,
} from "../../../constants";

// Extend the Palette interface to include 'focus' property
declare module "@mui/material/styles" {
  interface Palette {
    focus?: string;
  }

  interface PaletteOptions {
    focus?: string;
  }

  interface PaletteColor {
    focus?: string;
  }

  interface PaletteColorOptions {
    focus?: string;
  }
}

// Create the theme object first to access the palette
const { palette } = createTheme();

const customTheme: ThemeOptions = {
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          "& .MuiDataGrid-withBorderColor": {
            borderColor: "rgb(51, 54, 57)",
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
  overrides: {
    MuiFormLabel: {
      asterisk: {
        color: "#db3131",
        "&$error": {
          color: "#db3131",
        },
      },
    },
    MuiButton: {
      borderRadius: "50rem",
    },
    Select: {
      controlStyles: {
        borderRadius: 12,
        padding: "7px 4px",
      },
      menuStyle: {
        backgroundColor: "#000000",
        border: "1px solid rgb(51, 54, 57)",
        borderRadius: 12,
      },
    },
    AsyncCreatable: {
      controlStyles: {
        borderRadius: 12,
        padding: "7px 4px",
      },
      menuStyle: {
        backgroundColor: "#000000",
        border: "1px solid rgb(51, 54, 57)",
        borderRadius: 12,
      },
    },
    Async: {
      controlStyles: {
        borderRadius: 12,
        padding: "7px 4px",
      },
      menuStyle: {
        backgroundColor: "#000000",
        border: "1px solid rgb(51, 54, 57)",
        borderRadius: 12,
      },
    },
  },
  palette: {
    pinky: palette.augmentColor({ color: pink }) as { main: string; light: string; dark: string },
    summer: palette.augmentColor({ color: yellow }) as { main: string; light: string; dark: string },
    primary: {
      main: brandPrimary,
      focus: brandPrimaryFocus,  // Now valid because we've extended the Palette type
      dark: brandPrimaryFocus,
      contrastText: "#ffffff",
      text: "#212529",
    },
    secondary: {
      main: brandSecondary,
      focus: brandSecondaryFocus,  // Now valid
      contrastText: "#ffffff",
    },
    info: {
      main: "#0288d1",
      focus: "#01579b",
      light: "#03a9f4",
      dark: "#01579b",
      contrastText: "#ffffff",
      text: "#212529",
    },
    success: palette.augmentColor({ color: green }) as { main: string; light: string; dark: string },
    warning: palette.augmentColor({ color: yellow }) as { main: string; light: string; dark: string },
    danger: palette.augmentColor({ color: red }) as { main: string; light: string; dark: string },
    link: palette.augmentColor({ color: blue }) as { main: string; light: string; dark: string },
    light: {
      main: "#ffffff",
      focus: "#ffffff",  // Custom 'focus' property is allowed
      light: "#ffffff",
      dark: "#ffffff",
      contrastText: "#212529",
    },
    dark: {
      main: "#ffffff",
      focus: "#ffffff",  // Custom 'focus' property is allowed
      light: "#ffffff",
      dark: "#ffffff",
      contrastText: "#ffffff",
      text: "#212529",
    },
    grey: {
      main: "rgba(233, 233, 233, 0.1)",
      focus: "rgba(233, 233, 233, 0.1)",  // Custom 'focus' property is allowed
      light: "rgba(233, 233, 233, 0.1)",
      dark: "rgba(233, 233, 233, 0.1)",
      contrastText: "rgba(233, 233, 233, 0.1)",
      text: "rgba(233, 233, 233, 0.1)",
    },
    muted: {
      main: "rgba(222, 226, 230, 0.75)",
      focus: "rgba(222, 226, 230, 0.75)",
      light: "rgba(222, 226, 230, 0.75)",
      dark: "rgba(222, 226, 230, 0.75)",
      contrastText: "rgba(222, 226, 230, 0.75)",
      text: "rgba(222, 226, 230, 0.75)",
    },
    white: {
      main: "#ffffff",
      focus: "#ffffff",  // Custom 'focus' property is allowed
      light: "#ffffff",
      dark: "#ffffff",
      contrastText: "#ffffff",
      text: "#ffffff",
    },
    mode: "dark",
    type: "dark",
    background: {
      default: "#000000",
      paper: "rgb(22, 24, 28)",
    },
    mixins: {
      toolbar: {
        minHeight: 64,
      },
    },
  },
};

export default customTheme;